import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, XCircle, Clock, Link as LinkIcon, Copy, UserPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { APP_URL } from "@/lib/app-config";
import { format } from "date-fns";

interface RoleRequest {
  id: string;
  user_id: string;
  requested_role: string;
  status: string;
  metadata: any;
  created_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
}

const PendingRoleRequests = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<RoleRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("role_requests")
      .select("*")
      .order("created_at", { ascending: false });
    setRequests((data as any as RoleRequest[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleApprove = async (req: RoleRequest) => {
    if (!user) return;

    // Provider role can ONLY be granted via the credential review flow
    // (approve_provider RPC), which validates BAA + NPI + W-9. Approving a
    // provider role-request directly here would skip that gate, so we block it
    // and ask the admin to use the Provider Credentialing tab instead.
    if (req.requested_role === "provider") {
      toast({
        title: "Use the credential review flow",
        description:
          "Provider role can only be granted after credentials are submitted. Ask the applicant to complete onboarding at /provider/register, then approve them from the Providers tab.",
        variant: "destructive",
      });
      return;
    }

    // Update request status
    await supabase
      .from("role_requests")
      .update({ status: "approved", reviewed_by: user.id, reviewed_at: new Date().toISOString() })
      .eq("id", req.id);

    // Insert the role
    const { error: roleError } = await supabase
      .from("user_roles")
      .insert({ user_id: req.user_id, role: req.requested_role as any });
    if (roleError) {
      toast({ title: "Approval failed", description: roleError.message, variant: "destructive" });
      return;
    }

    // For pharmacy, also create the pharmacy + staff record if metadata has pharmacy info
    if (req.requested_role === "pharmacy" && req.metadata?.pharmacy_name) {
      const { data: pharmacy } = await supabase.from("partner_pharmacies").insert({
        name: req.metadata.pharmacy_name,
        email: req.metadata.email || "",
        state: req.metadata.state || null,
        city: req.metadata.city || null,
        address: req.metadata.address || null,
        zip_code: req.metadata.zip_code || null,
        phone: req.metadata.phone || null,
      }).select("id").single();

      if (pharmacy) {
        await supabase.from("pharmacy_staff").insert({
          user_id: req.user_id,
          pharmacy_id: pharmacy.id,
          role: "admin",
        });
      }
    }

    // For salesperson, create salesperson_settings
    if (req.requested_role === "salesperson") {
      await supabase.from("salesperson_settings").insert({
        user_id: req.user_id,
      });
    }

    // Send welcome email to approved salesperson or pharmacy partner
    if (req.requested_role === "salesperson" || req.requested_role === "pharmacy") {
      const { data: emails } = await supabase.rpc("get_user_emails");
      const recipientEmail = (emails || []).find((e) => e.user_id === req.user_id)?.email;
      if (recipientEmail) {
        const recipientName = [req.metadata?.first_name, req.metadata?.last_name].filter(Boolean).join(" ") || undefined;
        supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "role-approved-welcome",
            recipientEmail,
            idempotencyKey: `role-approved-${req.id}`,
            templateData: {
              recipientName,
              approvedRole: req.requested_role,
            },
          },
        });
      }
    }

    toast({ title: "Approved", description: `${req.requested_role} role granted.` });
    fetchRequests();
  };

  const handleReject = async (req: RoleRequest) => {
    if (!user) return;
    await supabase
      .from("role_requests")
      .update({ status: "rejected", reviewed_by: user.id, reviewed_at: new Date().toISOString() })
      .eq("id", req.id);
    toast({ title: "Rejected", description: "Request has been rejected." });
    fetchRequests();
  };

  const copyInviteLink = (role: string) => {
    const path = role === "provider" ? "/provider/register" : `/join/${role === "salesperson" ? "sales" : role}`;
    navigator.clipboard.writeText(`${APP_URL}${path}`);
    toast({ title: "Link copied!", description: `Invite link for ${role} copied to clipboard.` });
  };

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-6">
      {/* Invite Link Generator */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <LinkIcon size={18} /> Invite Links
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Share these links to let new team members register directly.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { role: "provider", label: "Provider", path: "/provider/register" },
              { role: "salesperson", label: "Salesperson", path: "/join/sales" },
              { role: "pharmacy", label: "Pharmacy", path: "/join/pharmacy" },
            ].map((item) => (
              <div key={item.role} className="flex items-center gap-2">
                <Input readOnly value={`${APP_URL}${item.path}`} className="text-xs" />
                <Button size="icon" variant="outline" onClick={() => copyInviteLink(item.role)}>
                  <Copy size={14} />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending Requests */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <UserPlus size={18} /> Role Requests
            {pendingCount > 0 && <Badge variant="destructive">{pendingCount} pending</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No role requests yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell className="font-medium">
                      {req.metadata?.first_name} {req.metadata?.last_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{req.requested_role}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {req.requested_role === "pharmacy" && req.metadata?.pharmacy_name}
                      {req.requested_role === "salesperson" && (req.metadata?.payout_method || "—")}
                    </TableCell>
                    <TableCell className="text-sm">
                      {format(new Date(req.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={req.status === "approved" ? "default" : req.status === "rejected" ? "destructive" : "secondary"}
                      >
                        {req.status === "pending" && <Clock size={12} className="mr-1" />}
                        {req.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {req.status === "pending" && (
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="default" onClick={() => handleApprove(req)} className="gap-1">
                            <CheckCircle2 size={14} /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleReject(req)} className="gap-1">
                            <XCircle size={14} /> Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingRoleRequests;
